/**
 * CPV-MT-01 F1 · the location choice on the CPV screens (ScopeContext). The header's Site ▸ Plant selector is the only
 * place a plant is chosen (owner ruling, 21 Oct 2026): no page has a plant filter or a plant picker of its own.
 *
 *   usePlantColumn()   when more than one plant is in view: {show, column, cell(row)} — a list's Plant column
 *   useActPlant()      a write's plant: the one plant in view the person may act on. With several in view, `needsOne` is
 *                      true and the write waits until the header names one; `body()` adds `{plant}` to the request body
 *   ActPlantNote       in a write dialog: the plant the write is made at (several plants in view), or "choose one plant
 *                      in the header" with a button that opens the header selector (`pick.none`: no plant in view allows it)
 */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Button } from 'antd';
import { MapPin } from 'lucide-react';
import { AlertBox } from '@/components/cpv-ui';
import { useScope } from '@/contexts/ScopeContext';

export function usePlantColumn() {
  const { multi, plantOf } = useScope();
  return useMemo(() => ({
    show: multi,
    column: { title: 'Plant' },
    cell: (row) => {
      const p = plantOf(row?.plant);
      return <td className="cu-nowrap" title={p?.name || undefined}>{p ? (p.code || p.name) : '—'}</td>;
    },
  }), [multi, plantOf]);
}

/** Insert the Plant column after the first column, when it shows. */
export const withPlantColumn = (plantCol, columns) => (plantCol.show ? [columns[0], plantCol.column, ...columns.slice(1)] : columns);

export function useActPlant() {
  const { actInView, home, byId, ready, multi } = useScope();
  const needsOne = actInView.length > 1;
  const plant = actInView.length === 1 ? actInView[0] : (needsOne || !ready ? null : home);
  return {
    plant, needsOne, multi,
    label: (id) => byId[id]?.name || String(id),
    /** The body key: sent whenever the location is known (a one-plant person's server default is the same plant). */
    body: () => (plant != null ? { plant } : {}),
  };
}

/** `what`: the write, in words ("issue a sheet"). Nothing shows for a person with one plant in view. */
export function ActPlantNote({ pick, what, testId = 'cpv-act-plant' }) {
  const { openChooser } = useScope();
  if (pick.needsOne || pick.none) {
    return (
      <AlertBox tone="info" testId={testId} title="Choose one plant">
        <span>
          {pick.none ? `None of the plants in view lets you ${what}.` : 'Several plants are in view.'}
          {` To ${what}, choose one plant in the location selector at the top of the page.`}
        </span>
        <div style={{ marginTop: 8 }}>
          <Button size="small" icon={<MapPin size={14} />} onClick={openChooser} data-testid={`${testId}-choose`}>Choose plant</Button>
        </div>
      </AlertBox>
    );
  }
  if (!pick.multi || pick.plant == null) return null;
  return (
    <div className="cs-field cs-wide" data-testid={testId}>
      <span>Plant</span>
      <span>{pick.label(pick.plant)}</span>
    </div>
  );
}

ActPlantNote.propTypes = { pick: PropTypes.object.isRequired, what: PropTypes.string.isRequired, testId: PropTypes.string };

/** The id of a row's plant ({id, …} or an id), or null. */
export const plantIdOf = (value) => (value && typeof value === 'object' ? value.id ?? null : value ?? null);

/** The rows (products, masters) of one plant; a row that names no plant is kept (the caller's own). */
export const forPlant = (rows, plant) => (plant == null ? rows : rows.filter((r) => r.plant == null || plantIdOf(r.plant) === plant));
